import moment from 'moment';
import {
  ActiveTicketCategory,
  CommunicationUserType,
  DriverSuggestionType,
  ObservationAffair,
  SuggestionAffair,
  SuggestionPriority,
  SuggestionStatus,
  SuggestionType,
  SuggestionUserResolution,
  SuggestionFormStep,
  SuggestionVisibility,
  UserRole,
  UserType,
} from 'src/app/models/enums';
import {
  IActiveTicket,
  IActiveTicketCategoryStyle,
  IActiveTicketGroup,
  IClient,
  ISuggestion,
  ISuggestionExpedition,
  ISuggestionIcon,
  ITicketDrawerRequest,
  ITrip,
  IUser,
} from 'src/app/models';
import { LiteralService } from 'src/app/services/literal/literal.service';
import { images } from 'src/images';

export const SUGGESTION_VISIBILITIES = Object.values(SuggestionVisibility);

export const DRIVER_SUGGESTION_TYPES = Object.values(DriverSuggestionType) as string[];

export function communicationUserTypeOfUser(user?: IUser | null): CommunicationUserType {
  return communicationUserTypeOf(user?.type);
}

export function communicationUserTypeOf(userType?: UserType): CommunicationUserType {
  return userType === UserType.Driver ? CommunicationUserType.DRIVERS : CommunicationUserType.USERS;
}

export function canCreateTicketFor(role: UserRole | string | null | undefined, userType: CommunicationUserType): boolean {
  if (role === UserRole.OPERATOR) return userType === CommunicationUserType.USERS;
  if (role === UserRole.DRIVER_MANAGER) return userType === CommunicationUserType.DRIVERS;
  return true;
}

export function defaultNewSuggestionTypeAndAffair(
  userType: CommunicationUserType,
): Pick<ISuggestion, 'type' | 'affair'> {
  return userType === CommunicationUserType.DRIVERS
    ? { type: DriverSuggestionType.OBSERVATION, affair: ObservationAffair.DRIVER_APP }
    : { type: SuggestionType.COMPLAINT, affair: SuggestionAffair.SERVICE_IMPROVEMENT };
}

export function suggestionVisibilityRank(visibility?: SuggestionVisibility | null): number {
  return SUGGESTION_VISIBILITIES.indexOf(visibility ?? SuggestionVisibility.PRIVATE);
}

export function isVisibleToPassengers(visibility?: SuggestionVisibility | null): boolean {
  return suggestionVisibilityRank(visibility) >= suggestionVisibilityRank(SuggestionVisibility.PASSENGERS);
}

export function isRaisingVisibilityToPassengers(
  previousVisibility: SuggestionVisibility | null | undefined,
  nextVisibility: SuggestionVisibility | null | undefined,
): boolean {
  return isVisibleToPassengers(nextVisibility) && !isVisibleToPassengers(previousVisibility);
}

export function suggestionVisibilityIcon(visibility?: SuggestionVisibility | null): string {
  return images.suggestions.visibility[visibility ?? SuggestionVisibility.PRIVATE];
}

export function suggestionPriorityIcon(priority?: SuggestionPriority | string | null): ISuggestionIcon | null {
  switch (priority) {
    case SuggestionPriority.LOW:
      return { src: images.suggestions.priority.low, filterClass: 'filter-primary-blue' };
    case SuggestionPriority.MEDIUM:
      return { src: images.suggestions.priority.medium, filterClass: '' };
    case SuggestionPriority.HIGH:
      return { src: images.suggestions.priority.high, filterClass: 'filter-secondary-orange-01' };
    case SuggestionPriority.HIGHEST:
      return { src: images.suggestions.priority.highest, filterClass: 'filter-secondary-red' };
    default:
      return null;
  }
}

export function suggestionStatusIcon(status?: SuggestionStatus | string | null): ISuggestionIcon {
  switch (status) {
    case SuggestionStatus.PENDING:
      return { src: images.suggestions.status.pending, filterClass: 'filter-primary-purple-light' };
    case SuggestionStatus.IN_PROGRESS:
      return { src: images.suggestions.status.inProgress, filterClass: '' };
    case SuggestionStatus.PROCESSED:
      return { src: images.suggestions.status.processed, filterClass: 'filter-secondary-green' };
    default:
      return { src: images.suggestions.status.rejected, filterClass: 'filter-neutrals-grey-04' };
  }
}

export function suggestionTypeIcon(type?: string | null): string {
  switch (type) {
    case DriverSuggestionType.OBJECT_FOUND:
      return images.suggestions.objectFound;
    case SuggestionType.OBJECT_LOST:
      return images.suggestions.objectLost;
    case SuggestionType.SUGGESTION:
      return images.suggestions.suggestion;
    case DriverSuggestionType.OBSERVATION:
      return images.suggestions.observation;
    case DriverSuggestionType.INCIDENCE:
      return images.suggestions.incidence;
    default:
      return images.suggestions.complaint;
  }
}

export function suggestionUserResolutionBadgeClass(
  userResolution?: SuggestionUserResolution | null,
): string {
  switch (userResolution) {
    case SuggestionUserResolution.RESOLVED:
      return 'badge-success';
    case SuggestionUserResolution.DISMISSED:
      return 'badge-neutral';
    default:
      return 'badge-ghost';
  }
}

export function suggestionUserTypeOf(type: string): CommunicationUserType {
  return DRIVER_SUGGESTION_TYPES.includes(type)
    ? CommunicationUserType.DRIVERS
    : CommunicationUserType.USERS;
}

export function suggestionUrl(id: number, type: string): string {
  return `/suggestions/${suggestionUserTypeOf(type)}/${id}`;
}

export function suggestionWithoutSuggesterLiteral(suggestion: ISuggestion): string {
  return suggestion.systemCreated ? 'suggestions.systemUser' : 'suggestions.anonymousUser';
}

export function suggestionExpeditionLabel(expedition?: ISuggestionExpedition | null): string {
  if (!expedition) return '';
  return [
    expedition.dateTime ? moment(expedition.dateTime).format('DD/MM HH:mm') : null,
    expedition.name,
    expedition.vehicleName,
  ]
    .filter((part) => !!part)
    .join(' · ');
}

export function buildNewSuggestion(
  userType: CommunicationUserType,
  prefill: Partial<ISuggestion> = {},
): ISuggestion {
  return {
    ...defaultNewSuggestionTypeAndAffair(userType),
    description: '',
    comments: '',
    priority: SuggestionPriority.MEDIUM,
    status: SuggestionStatus.PENDING,
    visibility: SuggestionVisibility.PRIVATE,
    publicMessage: '',
    expedition: null,
    ...prefill,
  };
}

export function toSuggestionExpedition(trip: ITrip): ISuggestionExpedition {
  return {
    id: trip.id!,
    name: trip.name,
    dateTime: trip.dateTime,
    status: trip.status,
    vehicleName: trip.vehicle?.name,
  };
}

export function buildNewTicketFromTrip(trip: ITrip, passenger?: IUser): ITicketDrawerRequest {
  const userType = passenger ? CommunicationUserType.USERS : CommunicationUserType.DRIVERS;
  return {
    userType,
    startStep: passenger ? SuggestionFormStep.DETAILS : SuggestionFormStep.LOCATION,
    newSuggestion: buildNewSuggestion(userType, {
      client: trip.managerClientId ? ({ id: trip.managerClientId } as IClient) : undefined,
      town: trip.town,
      service: trip.service,
      expedition: toSuggestionExpedition(trip),
      userSuggester: passenger ?? trip.driver,
    }),
  };
}

export function buildNewTicketForUser(user: IUser, listUserType: UserType): ITicketDrawerRequest {
  const userType = communicationUserTypeOf(listUserType);
  return {
    userType,
    startStep: SuggestionFormStep.LOCATION,
    newSuggestion: buildNewSuggestion(userType, { userSuggester: user }),
  };
}

const ACTIVE_TICKET_TYPE_ORDER: string[] = [
  DriverSuggestionType.INCIDENCE,
  SuggestionType.COMPLAINT,
  SuggestionType.SUGGESTION,
  SuggestionType.OBJECT_LOST,
  DriverSuggestionType.OBSERVATION,
  DriverSuggestionType.OBJECT_FOUND,
];

export const ACTIVE_TICKET_CATEGORY_ORDER = [
  ActiveTicketCategory.INCIDENT,
  ActiveTicketCategory.OBSERVATION,
  ActiveTicketCategory.OBJECT_FOUND,
];

export const ACTIVE_TICKET_CATEGORY_STYLES: Record<ActiveTicketCategory, IActiveTicketCategoryStyle> = {
  [ActiveTicketCategory.INCIDENT]: {
    icon: images.suggestions.activeTickets,
    iconClass: 'filter-secondary-red',
    compactClass: 'bg-secondary-red/15',
    textClass: 'text-secondary-red',
    badgeClass: 'bg-secondary-red',
  },
  [ActiveTicketCategory.OBSERVATION]: {
    icon: images.suggestions.info,
    iconClass: 'filter-primary-blue',
    compactClass: 'bg-primary-blue/15',
    textClass: 'text-primary-blue',
    badgeClass: 'bg-primary-blue',
  },
  [ActiveTicketCategory.OBJECT_FOUND]: {
    icon: images.suggestions.info,
    iconClass: 'filter-secondary-green-dark',
    compactClass: 'bg-secondary-green-dark/15',
    textClass: 'text-secondary-green-dark',
    badgeClass: 'bg-secondary-green-dark',
  },
};

export function activeTicketCategoryOf(type: string): ActiveTicketCategory {
  if (type === DriverSuggestionType.OBSERVATION) return ActiveTicketCategory.OBSERVATION;
  if (type === DriverSuggestionType.OBJECT_FOUND) return ActiveTicketCategory.OBJECT_FOUND;
  return ActiveTicketCategory.INCIDENT;
}

export function activeTicketsCategory(tickets: IActiveTicket[]): ActiveTicketCategory {
  const categories = new Set(tickets.map((ticket) => activeTicketCategoryOf(ticket.type)));
  return ACTIVE_TICKET_CATEGORY_ORDER.find((category) => categories.has(category)) ?? ActiveTicketCategory.INCIDENT;
}

export function groupActiveTicketsByCategory(tickets: IActiveTicket[]): IActiveTicketGroup[] {
  return ACTIVE_TICKET_CATEGORY_ORDER.map((category) => ({
    category,
    tickets: tickets.filter((ticket) => activeTicketCategoryOf(ticket.type) === category),
  })).filter((group) => group.tickets.length > 0);
}

export function incidentTickets(tickets: IActiveTicket[] = []): IActiveTicket[] {
  return tickets.filter((ticket) => activeTicketCategoryOf(ticket.type) === ActiveTicketCategory.INCIDENT);
}

export function activeTicketTypeCounts(tickets: IActiveTicket[]): { type: string; count: number }[] {
  const countByType = new Map<string, number>();
  tickets.forEach((ticket) => countByType.set(ticket.type, (countByType.get(ticket.type) ?? 0) + 1));
  return ACTIVE_TICKET_TYPE_ORDER.filter((type) => countByType.has(type)).map((type) => ({ type, count: countByType.get(type)! }));
}

export function activeTicketTypeCountKey(type: string, count: number): string {
  return `suggestions.typeCounts.${type.toLowerCase()}.${count === 1 ? 'one' : 'many'}`;
}

export function activeTicketsSummary(tickets: IActiveTicket[], literalService: LiteralService): string {
  return activeTicketTypeCounts(tickets)
    .map(({ type, count }) => literalService.get(activeTicketTypeCountKey(type, count), true, { count }))
    .join('</br>');
}
